import { Component, OnInit, Input, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { GetBankAccountValues, ClearBankAccountValues, AcknowledgeOcrolusErrors } from 'app/store/bank-account/bank-account.actions';
import { BankAccountState } from 'app/store/bank-account/bank-account.state';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { AlternateTransactionsComponent } from '../alternate-transactions/alternate-transactions.component';
import { LocaleSettingService } from 'app/services/i18n/locale-setting.service';

import { BorrowerValue } from 'app/models/dto/borrower-value.model';
import { map, takeUntil } from 'rxjs/operators';
import { MonthlyTotalsDialogComponent } from '../monthly-totals-dialog/monthly-totals-dialog.component';
import { BankAccountApiPayload } from 'app/services/bank-account.service';
import { EventBusService } from 'app/modules/shared/services/event-bus.service';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { BorrowerValueState } from 'app/store/borrower-value/borrower-value.state';

@Component({
  selector: 'app-monthly-totals-card-smart',
  templateUrl: './monthly-totals-card-smart.component.html',
  styleUrls: ['./monthly-totals-card-smart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MonthlyTotalsCardSmartComponent implements OnInit, OnDestroy {

  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;


  @Input() borrowerId: number;
  @Input() inContainer: boolean = true;
  @Input() basicStyle: boolean = false;

  bankValues$:  Observable<BorrowerValue[] | undefined>;
  alternateLenderTransactions$: Observable<({ transactionDate: string, amount: string, description: string } | undefined)[]>;
  bankAccountsOcrolusStatus$: Observable<BankAccountApiPayload | undefined>;
  canAcknowledgeDocErrors$: Observable<boolean>;
  bankConnection$: Observable<BorrowerValue | null>;
  destroyed$ = new Subject<boolean>();

  constructor(
    private store: Store,
    public dialog: MatDialog,
    private localeSetting: LocaleSettingService,
    private eventBusService: EventBusService,
  ) { }

  ngOnInit(): void {
    this.store.dispatch(new GetBankAccountValues(this.borrowerId));
    this.bankValues$ = this.store.select(BankAccountState.bankValues).pipe(map(x => x(this.borrowerId)));
    this.alternateLenderTransactions$ = this.store.select(BankAccountState.alternateLenderTransactions).pipe(map(x => x(this.borrowerId)));
    this.bankAccountsOcrolusStatus$ = this.store.select(BankAccountState.bankAccountsStatus).pipe(map(x => x(this.borrowerId)));
    this.bankConnection$ = this.store.select(BorrowerValueState.valueByAlias)
      .pipe(takeUntil(this.destroyed$), map(filterFn => filterFn('finicityConnected')));
    this.canAcknowledgeDocErrors$ = this.lenderUser$.pipe(map((user) => user.permissions.includes('acknowledgeDocErrors')));
  }

  ngOnDestroy(): void {
    this.store.dispatch(new ClearBankAccountValues(this.borrowerId));
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  goToDocuments(): void {
    this.eventBusService.publish(new this.eventBusService.types.GoToDocumentsTabEvent());
  }

  onOcrolusErrorDismiss(canAcknowledgeDocErrors: boolean): void {
    if (canAcknowledgeDocErrors) {
      this.store.dispatch(new AcknowledgeOcrolusErrors(this.borrowerId));
    }
  }

  showMonthlyTotalsDialogEdit(): void {
    const dialogRef = this.dialog.open(MonthlyTotalsDialogComponent, {
      data: { borrowerId: this.borrowerId },
      width: '1148px',
      panelClass: 'monthly-totals-dialog-container'
    });
  }

  showAlternateTransactionsDialog(alternateTransactions): void {
    const dialogRef = this.dialog.open(AlternateTransactionsComponent, {
      data: alternateTransactions,
      width: '850px',
    });
  }
}
